/**
 * PATH: packages/email-hub/src/feature/components/SearchSyncBanner.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENTS-SEARCHSYNCBANNER-001
 * PURPOSE: Dismissible inline banner (G2) shown above search results
 *          while the historical backfill is mid-walk: "older emails
 *          may not appear in search yet".
 * WHY: Zero-result searches during first-connect look like broken
 *      search; the signal already exists client-side in the backfill
 *      walker's localStorage state ? no backend call needed.
 *      Dismissal is per-session (sessionStorage) so a long-running
 *      backfill doesn't nag every page load but re-surfaces in a
 *      fresh session while still incomplete.
 * RELATED:
 *  - lib/syncSearchBanner.ts: pure render gate (unit-tested).
 *  - hooks/useEmailBackfill.ts: useBackfillStateView state source.
 */

import { useState } from 'react'
import { Info, X } from 'lucide-react'
import { useBackfillStateView } from '../hooks/useEmailBackfill'
import {
  shouldShowBannerLiveSearchCta,
  shouldShowSyncSearchBanner,
} from '../lib/syncSearchBanner'

const DISMISS_KEY = 'qt-email-sync-search-banner-dismissed'

function loadDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1'
  } catch {
    return false
  }
}

interface SearchSyncBannerProps {
  organisationId: number | null | undefined
  userId: number | null | undefined
  /** True while the operator has an active search (debounced). */
  searchActive: boolean
  /** G2 live-search: local recall is sparse and the fanout is available. */
  liveSearchOffered?: boolean
  /** G2 live-search: fire the explicit Outlook fanout. */
  onLiveSearch?: () => void
}

export function SearchSyncBanner({
  organisationId,
  userId,
  searchActive,
  liveSearchOffered = false,
  onLiveSearch,
}: SearchSyncBannerProps) {
  const { state } = useBackfillStateView(organisationId, userId)
  const [dismissed, setDismissed] = useState(loadDismissed)

  if (!shouldShowSyncSearchBanner({ state, searchActive, dismissed })) {
    return null
  }

  return (
    <div
      className="flex items-center gap-2 mt-2 px-2.5 py-1.5 text-xs rounded-md border border-amber-300/60 bg-amber-50 text-amber-800 dark:border-amber-700/50 dark:bg-amber-900/20 dark:text-amber-200"
      role="status"
      data-testid="email-sync-search-banner"
    >
      <Info className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="flex-1">
        Mailbox still syncing — older emails may not appear in search yet.
        {shouldShowBannerLiveSearchCta({
          bannerShown: true,
          liveSearchOffered,
        }) && onLiveSearch && (
          <>
            {' '}
            <button
              type="button"
              onClick={onLiveSearch}
              className="underline hover:no-underline font-medium"
              data-testid="email-sync-banner-live-search-cta"
            >
              Search Outlook for older mail
            </button>
          </>
        )}
      </span>
      <button
        type="button"
        onClick={() => {
          setDismissed(true)
          try {
            sessionStorage.setItem(DISMISS_KEY, '1')
          } catch {
            /* no storage ? dismissal is state-only for this mount */
          }
        }}
        className="h-4 w-4 rounded flex items-center justify-center hover:bg-amber-200/60 dark:hover:bg-amber-800/40"
        aria-label="Dismiss sync notice"
        title="Dismiss"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  )
}
